import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { CheckCircle, Truck, ArrowRight, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { getPaymentStatus } from '../lib/api';

export default function BookingSuccess() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const [paymentStatus, setPaymentStatus] = useState('checking');
  const [attempts, setAttempts] = useState(0);

  useEffect(() => {
    const pollStatus = async () => {
      if (!sessionId || attempts >= 10) return;

      try {
        const response = await getPaymentStatus(sessionId);
        const status = response.data;

        if (status.payment_status === 'paid') {
          setPaymentStatus('success');
        } else if (status.status === 'expired') {
          setPaymentStatus('expired');
        } else {
          // Continue polling
          setAttempts(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        }
      } catch (error) {
        console.error('Error checking payment status:', error);
        if (attempts < 5) {
          setAttempts(prev => prev + 1);
          setTimeout(pollStatus, 2000);
        } else {
          setPaymentStatus('error');
        }
      }
    };

    pollStatus();
  }, [sessionId, attempts]);

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
      <div className="max-w-lg w-full">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          {paymentStatus === 'checking' && (
            <div className="animate-fade-in" data-testid="payment-checking">
              <div className="w-20 h-20 bg-blue-50 rounded-full flex items-center justify-center mx-auto mb-6">
                <Loader2 className="w-10 h-10 text-[#38B6FF] animate-spin" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3 font-['Outfit']">
                Confirming Your Payment
              </h1>
              <p className="text-slate-600 mb-6">
                Please wait while we confirm your payment...
              </p>
            </div>
          )}

          {paymentStatus === 'success' && (
            <div className="animate-fade-in" data-testid="payment-success">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3 font-['Outfit']">
                Booking Confirmed!
              </h1>
              <p className="text-slate-600 mb-6">
                Thank you for your booking! We've sent a confirmation email with all the details.
                Our team will contact you shortly to confirm pickup arrangements.
              </p>
              <div className="bg-slate-50 rounded-xl p-4 mb-8">
                <p className="text-sm text-slate-500 mb-1">What happens next?</p>
                <ul className="text-left text-sm text-slate-600 space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    You'll receive a confirmation email shortly
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Our team will contact you to confirm pickup date and time
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                    Your vehicle will be transported safely to the destination
                  </li>
                </ul>
              </div>
              <Link to="/">
                <Button className="bg-[#38B6FF] hover:bg-blue-500 text-white px-8" data-testid="back-home-btn">
                  Back to Home
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {paymentStatus === 'expired' && (
            <div className="animate-fade-in" data-testid="payment-expired">
              <div className="w-20 h-20 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Truck className="w-10 h-10 text-yellow-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3 font-['Outfit']">
                Session Expired
              </h1>
              <p className="text-slate-600 mb-6">
                Your payment session has expired. Please try booking again.
              </p>
              <Link to="/booking">
                <Button className="bg-[#38B6FF] hover:bg-blue-500 text-white px-8" data-testid="try-again-btn">
                  Try Again
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}

          {paymentStatus === 'error' && (
            <div className="animate-fade-in" data-testid="payment-error">
              <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Truck className="w-10 h-10 text-red-600" />
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-3 font-['Outfit']">
                Something Went Wrong
              </h1>
              <p className="text-slate-600 mb-6">
                We couldn't confirm your payment status. Please check your email for confirmation or contact support.
              </p>
              <Link to="/">
                <Button className="bg-[#38B6FF] hover:bg-blue-500 text-white px-8" data-testid="contact-support-btn">
                  Back to Home
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
